import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Bot, Save } from 'lucide-react';

interface AISettings {
  auto_generate_descriptions: boolean;
  auto_seo_analysis: boolean;
  auto_approve_threshold: number;
  seo_min_score: number;
  default_ai_prompt: string;
}

export const AIAutomationSettings = () => {
  const [settings, setSettings] = useState<AISettings>({
    auto_generate_descriptions: false,
    auto_seo_analysis: true,
    auto_approve_threshold: 80,
    seo_min_score: 70,
    default_ai_prompt: 'Generate a professional product description that highlights key features, benefits, and technical specifications. Focus on luxury, quality, and innovation.'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // In a real implementation, this would load from a settings table
      // For now, we'll use localStorage as a demo
      const saved = localStorage.getItem('ai_automation_settings');
      if (saved) {
        setSettings(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save to localStorage (in production, this would be a database table)
      localStorage.setItem('ai_automation_settings', JSON.stringify(settings));
      toast.success('AI automation settings saved successfully');
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          AI Automation Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Auto-Generate Descriptions</Label>
              <p className="text-sm text-muted-foreground">
                Automatically generate AI descriptions for new products
              </p>
            </div>
            <Switch
              checked={settings.auto_generate_descriptions}
              onCheckedChange={(checked) => 
                setSettings({ ...settings, auto_generate_descriptions: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Auto SEO Analysis</Label>
              <p className="text-sm text-muted-foreground">
                Run SEO analysis automatically on product uploads
              </p>
            </div>
            <Switch
              checked={settings.auto_seo_analysis}
              onCheckedChange={(checked) => 
                setSettings({ ...settings, auto_seo_analysis: checked })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="auto-approve">Auto-Approve Threshold</Label>
            <p className="text-sm text-muted-foreground">
              Products with SEO scores above this threshold will be auto-approved
            </p>
            <Input
              id="auto-approve"
              type="number"
              min="0"
              max="100"
              value={settings.auto_approve_threshold}
              onChange={(e) => 
                setSettings({ ...settings, auto_approve_threshold: parseInt(e.target.value) || 80 })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="seo-min">Minimum SEO Score</Label>
            <p className="text-sm text-muted-foreground">
              Minimum acceptable SEO score for product submissions
            </p>
            <Input
              id="seo-min"
              type="number"
              min="0"
              max="100"
              value={settings.seo_min_score}
              onChange={(e) => 
                setSettings({ ...settings, seo_min_score: parseInt(e.target.value) || 70 })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ai-prompt">Default AI Prompt Template</Label>
            <p className="text-sm text-muted-foreground">
              Template used for generating product descriptions
            </p>
            <Textarea
              id="ai-prompt"
              rows={5}
              value={settings.default_ai_prompt}
              onChange={(e) => 
                setSettings({ ...settings, default_ai_prompt: e.target.value })
              }
            />
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg text-sm space-y-2">
          <p className="font-medium">Current Configuration:</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>• Auto-generate: {settings.auto_generate_descriptions ? 'Enabled' : 'Disabled'}</li>
            <li>• Auto SEO: {settings.auto_seo_analysis ? 'Enabled' : 'Disabled'}</li>
            <li>• Auto-approve at: {settings.auto_approve_threshold}+ SEO score</li>
            <li>• Minimum SEO: {settings.seo_min_score}</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
